import i18n from "@/i18n";
/**
 * Prompt 12K-2 — Day Entry Cell Editor (LOCAL CORE / no live network).
 *
 * Reusable local editor for an APPLICABLE Current Plan cell that
 * contains exactly ONE canonical Day Entry and NO Shift. Displays
 * the canonical Day Entry code; on interaction emits strictly local
 * update / delete intents to the parent for future 12K-3 live
 * integration.
 *
 * Prompt 12K-2 §16 · §17 · §18 · §22 · §31 — MUST NOT call any
 * HTTP client, MUST NOT mutate Schedule state, MUST NOT silently
 * cross-replace Day Entry with Shift or vice-versa. Live network
 * integration belongs to Prompt 12K-3.
 */
import React, {
    useEffect,
    useRef,
    useState,
} from "react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import {
    parseQuickShiftInput,
} from "@/components/workforce/workforceHelpers";
import {
    normalizeDayEntryCode,
} from "@/components/workforce/PlanCellEditor";

// Prompt 12K-2 §20 — exact copy for time-over-existing-Day-code
// protection. Prevents silent cross-type replacement.
export const DAY_ENTRY_TIME_BLOCKED_GUIDANCE =
    "Remove the day code before adding a shift.";

// Prompt 12K-2 §21 — exact copy for invalid Day-Entry-only input
// that is neither a Day code nor a Shift time expression.
export const DAY_ENTRY_INVALID_GUIDANCE =
    "Use a day code: V, X, TS, VV.";

// Prompt 12K-2 §16 — eligible cells only: applicable, exactly one
// Day Entry, no Shift.
function _isEligible(cell) {
    if (!cell || cell.applicable !== true) return false;
    if (!Array.isArray(cell.shifts) || cell.shifts.length !== 0) return false;
    if (!Array.isArray(cell.day_entries)) return false;
    if (cell.day_entries.length !== 1) return false;
    return true;
}

export function DayEntryCellEditor({
    cell,
    onIntent,
    isPending,
    children,
    cellKey,
    cancelEditVersion,
}) {
    const eligible = _isEligible(cell);
    const dayEntry = eligible ? cell.day_entries[0] : null;

    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState("");

    const inputRef = useRef(null);
    // Prompt 12K-2 §15 — mirror the existing zero-Shift blur
    // semantics: deferred by one browser task turn.
    const blurTimerRef = useRef(null);
    const committedRef = useRef(false);
    const cancelSeenRef = useRef(cancelEditVersion);

    const cancelBlurTimer = () => {
        if (blurTimerRef.current !== null) {
            clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            cancelBlurTimer();
        };
    }, []);

    // Prompt 12K-2 §26 — parent-controlled opaque bump cancels any
    // active edit and any pending deferred blur commit, no intent.
    useEffect(() => {
        if (cancelSeenRef.current === cancelEditVersion) return;
        cancelSeenRef.current = cancelEditVersion;
        cancelBlurTimer();
        committedRef.current = true;
        setIsEditing(false);
        setText("");
    }, [cancelEditVersion]);

    useEffect(() => {
        if (!isEditing) return;
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        try {
            el.select();
        } catch (_e) {
            /* jsdom fallback — selection is optional. */
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing]);

    const openEditor = () => {
        if (!dayEntry) return;
        committedRef.current = false;
        cancelBlurTimer();
        // Prompt 12K-2 §17 — editing begins with the current
        // canonical code.
        setText(dayEntry.code);
        setIsEditing(true);
    };

    const emitUpdate = (normalizedCode, navigate) => {
        // Prompt 12K-2 §27 — pending guard.
        if (isPending) return;
        if (committedRef.current) return;
        committedRef.current = true;
        cancelBlurTimer();
        if (typeof onIntent === "function") {
            // Prompt 12K-2 §18 — frozen update intent shape. Do NOT
            // strip the canonical object; 12K-3 needs day_entry_id
            // and revision for PATCH mapping.
            onIntent({
                mode: "update-day-entry",
                code: normalizedCode,
                dayEntry,
                navigate,
                source: "day-entry-editor",
            });
        }
        setIsEditing(false);
        setText("");
    };

    const emitDelete = () => {
        // Prompt 12K-2 §27 — pending guard.
        if (isPending) return;
        if (!dayEntry) return;
        if (typeof onIntent === "function") {
            // Prompt 12K-2 §22 — frozen delete intent shape. No
            // `navigate` field. Canonical object carried through
            // so 12K-3 can map day_entry_id + revision to DELETE.
            onIntent({
                mode: "delete-day-entry",
                dayEntry,
                source: "day-entry-editor",
            });
        }
    };

    const showTimeBlockedGuidance = () => {
        toast.error(i18n.t("wf.DayEntryCellEditor.DAY_ENTRY_TIME_BLOCKED_GUIDANCE", DAY_ENTRY_TIME_BLOCKED_GUIDANCE));
    };

    const showInvalidGuidance = () => {
        toast.error(i18n.t("wf.DayEntryCellEditor.DAY_ENTRY_INVALID_GUIDANCE", DAY_ENTRY_INVALID_GUIDANCE));
    };

    // Prompt 12K-2 §22 · §23 — non-editing wrapper key handling.
    // Enter opens the editor. Delete / Backspace emit delete-intent.
    const handleWrapperKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            openEditor();
            return;
        }
        if (e.key === "Delete" || e.key === "Backspace") {
            // Prompt 12K-2 §22 — non-editing Delete / Backspace
            // emits delete-day-entry. No optimistic removal.
            e.preventDefault();
            emitDelete();
        }
    };

    const handleWrapperClick = () => {
        openEditor();
    };

    // Prompt 12K-2 §17 · §18 · §19 · §20 · §21 · §24 · §25 · §26 —
    // inline input key handling.
    const handleInputKeyDown = (e) => {
        if (e.key === "Escape") {
            // Prompt 12K-2 §26 — restore / cancel local edit;
            // canonical Day Entry stays displayed; no intent.
            e.preventDefault();
            cancelBlurTimer();
            committedRef.current = true;
            setIsEditing(false);
            setText("");
            return;
        }

        // Prompt 12K-2 §23 — while editing, Delete / Backspace edit
        // text normally and MUST NOT emit delete-day-entry.
        if (e.key === "Delete" || e.key === "Backspace") {
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();
            _handleCommit(null);
            return;
        }

        if (e.key === "Tab") {
            e.preventDefault();
            const navigate = e.shiftKey ? "previous" : "next";
            _handleCommit(navigate);
        }
    };

    const _handleCommit = (navigate) => {
        const raw = typeof text === "string" ? text : "";
        const trimmed = raw.trim();
        if (trimmed === "") {
            // Empty commit → cancel silently. Mirrors zero-Shift
            // blank Enter / Tab semantics.
            cancelBlurTimer();
            committedRef.current = true;
            setIsEditing(false);
            setText("");
            return;
        }
        const normalized = normalizeDayEntryCode(raw);
        if (normalized !== null) {
            // Prompt 12K-2 §19 · §24 — unchanged normalized code
            // emits no update intent.
            if (dayEntry && normalized === dayEntry.code) {
                cancelBlurTimer();
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            // Prompt 12K-2 §18 · §24 · §25 — valid changed code
            // emits update-day-entry with navigate as directed.
            emitUpdate(normalized, navigate);
            return;
        }
        // Prompt 12K-2 §20 — time input over an existing Day Entry
        // must NOT emit any Shift or update intent; explicit copy.
        const parsedShift = parseQuickShiftInput(raw, cell.date);
        if (parsedShift !== null) {
            cancelBlurTimer();
            showTimeBlockedGuidance();
            return;
        }
        // Prompt 12K-2 §21 — any other invalid input: Day-code
        // guidance only. No mutation intent.
        cancelBlurTimer();
        showInvalidGuidance();
    };

    // Prompt 12K-2 §15 — preserve existing zero-Shift editor blur
    // semantics EXACTLY: deferred by one browser task turn.
    const handleInputBlur = () => {
        cancelBlurTimer();
        const capturedText = text;
        const capturedDate = cell.date;
        const capturedDayEntry = dayEntry;
        blurTimerRef.current = setTimeout(() => {
            blurTimerRef.current = null;
            if (committedRef.current) return;
            const trimmed =
                typeof capturedText === "string" ? capturedText.trim() : "";
            if (trimmed === "") {
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            const normalized = normalizeDayEntryCode(capturedText);
            if (normalized !== null) {
                if (capturedDayEntry && normalized === capturedDayEntry.code) {
                    committedRef.current = true;
                    setIsEditing(false);
                    setText("");
                    return;
                }
                emitUpdate(normalized, null);
                return;
            }
            const parsedShift = parseQuickShiftInput(
                capturedText, capturedDate,
            );
            if (parsedShift !== null) {
                showTimeBlockedGuidance();
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            showInvalidGuidance();
            committedRef.current = true;
            setIsEditing(false);
            setText("");
        }, 0);
    };

    if (!eligible) {
        return <>{children}</>;
    }

    if (isEditing) {
        return (
            <Input
                ref={inputRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                data-testid="day-entry-cell-editor-input"
                aria-label={i18n.t("wf.dayEntryCellEditor.dayEntryQuickInput", "Day entry quick input")}
            />
        );
    }

    // Prompt 12K-2 §16 · §22 — non-editing focusable wrapper displays
    // the canonical code and owns Delete / Backspace delete intent.
    return (
        <span
            tabIndex={0}
            role="button"
            onClick={handleWrapperClick}
            onKeyDown={handleWrapperKeyDown}
            data-testid="day-entry-cell-editor-wrapper"
            data-workforce-day-entry-cell-key={cellKey}
        >
            {children}
        </span>
    );
}

export default DayEntryCellEditor;
